import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Search, 
  Edit, 
  Trash2, 
  Eye, 
  Download, 
  Upload, 
  ToggleLeft, 
  ToggleRight,
  Star,
  Phone,
  Mail,
  Globe,
  MapPin,
  Building,
  Camera,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';
import { 
  getAdminVendors, 
  createAdminVendor, 
  updateAdminVendor, 
  deleteAdminVendor, 
  toggleVendorStatus,
  exportVendorData
} from '../../lib/admin';
import VendorFormModal from './VendorFormModal';
import type { AdminVendor, VendorFormData } from '../../types/admin';

export default function VendorManagement() {
  const [vendors, setVendors] = useState<AdminVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showFormModal, setShowFormModal] = useState(false);
  const [editingVendor, setEditingVendor] = useState<AdminVendor | null>(null);
  const [selectedVendor, setSelectedVendor] = useState<AdminVendor | null>(null);

  useEffect(() => {
    loadVendors();
  }, []);

  const loadVendors = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getAdminVendors();
      setVendors(data);
    } catch (err) {
      console.error('Error loading vendors:', err);
      setError('Failed to load vendors');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateVendor = async (vendorData: VendorFormData) => {
    try {
      await createAdminVendor(vendorData);
      setShowFormModal(false);
      await loadVendors();
    } catch (err) {
      console.error('Error creating vendor:', err);
      throw err;
    }
  };

  const handleUpdateVendor = async (vendorData: VendorFormData) => {
    if (!editingVendor) return;
    
    try {
      await updateAdminVendor(editingVendor.id, vendorData);
      setEditingVendor(null);
      setShowFormModal(false);
      await loadVendors();
    } catch (err) {
      console.error('Error updating vendor:', err);
      throw err;
    }
  };

  const handleDeleteVendor = async (vendorId: string, vendorName: string) => {
    if (!confirm(`Are you sure you want to delete ${vendorName}? This action cannot be undone.`)) {
      return;
    }

    try {
      await deleteAdminVendor(vendorId);
      await loadVendors();
    } catch (err) {
      console.error('Error deleting vendor:', err);
      setError('Failed to delete vendor');
    }
  };

  const handleToggleStatus = async (vendorId: string, currentStatus: boolean) => {
    try {
      await toggleVendorStatus(vendorId, !currentStatus);
      await loadVendors();
    } catch (err) {
      console.error('Error toggling vendor status:', err);
      setError('Failed to update vendor status');
    }
  };

  const filteredVendors = vendors.filter(vendor => {
    const matchesSearch = 
      vendor.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.contact_person.toLowerCase().includes(searchTerm.toLowerCase()) ||
      vendor.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesCategory = categoryFilter === 'all' || 
      vendor.service_categories.some(cat => cat.toLowerCase().includes(categoryFilter.toLowerCase()));
    
    const matchesStatus = statusFilter === 'all' || 
      (statusFilter === 'active' && vendor.is_active) ||
      (statusFilter === 'inactive' && !vendor.is_active);
    
    return matchesSearch && matchesCategory && matchesStatus;
  });

  const serviceCategories = [
    'Photography', 'Videography', 'Catering', 'Music & DJ', 'Flowers & Decor', 
    'Transportation', 'Makeup & Beauty', 'Wedding Planning', 'Lighting', 'Security'
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">Vendor Management</h2>
          <p className="text-gray-300">Manage wedding vendors and service providers</p>
        </div>
        <div className="flex items-center space-x-4">
          <button
            onClick={() => exportVendorData()}
            className="flex items-center space-x-2 px-4 py-2 bg-gray-700 text-white rounded-md hover:bg-gray-600"
          >
            <Download className="w-4 h-4" />
            <span>Export</span>
          </button>
          <button
            onClick={() => setShowFormModal(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600"
          >
            <Plus className="w-4 h-4" />
            <span>Add Vendor</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-900/50 border border-red-800 text-red-200 p-3 rounded-md flex items-center space-x-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Filters */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search vendors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 w-full px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
            />
          </div>

          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            className="px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
          >
            <option value="all">All Categories</option>
            {serviceCategories.map(category => (
              <option key={category} value={category.toLowerCase()}>
                {category}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 bg-gray-900/50 border border-gray-700 text-white rounded-md focus:ring-primary focus:border-primary"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <div className="text-sm text-gray-300 flex items-center">
            Showing {filteredVendors.length} of {vendors.length} vendors
          </div>
        </div>
      </div>

      {/* Vendors Table */}
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Vendor Details
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Contact Information
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Services & Rating
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-xs font-medium text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {filteredVendors.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center">
                    <Building className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-400 text-lg">No vendors found</p>
                    <p className="text-gray-500 text-sm">Add your first vendor to get started</p>
                  </td>
                </tr>
              ) : (
                filteredVendors.map((vendor) => (
                  <tr key={vendor.id} className="hover:bg-gray-700/50">
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-3">
                        {vendor.photos.length > 0 && (
                          <img
                            src={vendor.photos[0]}
                            alt={vendor.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium text-white">{vendor.name}</p>
                          <p className="text-sm text-gray-400">{vendor.contact_person}</p>
                          {vendor.business_license && (
                            <p className="text-xs text-gray-500">License: {vendor.business_license}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-1">
                        <div className="flex items-center space-x-2 text-sm text-gray-300">
                          <Mail className="w-4 h-4" />
                          <span>{vendor.email}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-300">
                          <Phone className="w-4 h-4" />
                          <span>{vendor.phone}</span>
                        </div>
                        <div className="flex items-center space-x-2 text-sm text-gray-300">
                          <MapPin className="w-4 h-4" />
                          <span className="truncate max-w-32">{vendor.address}</span>
                        </div>
                        {vendor.website && (
                          <div className="flex items-center space-x-2 text-sm text-gray-300">
                            <Globe className="w-4 h-4" />
                            <a 
                              href={vendor.website} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-primary hover:text-primary-600 truncate max-w-32"
                            >
                              Website
                            </a>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        <div className="flex flex-wrap gap-1">
                          {vendor.service_categories.slice(0, 2).map((category, index) => (
                            <span
                              key={index}
                              className="px-2 py-1 bg-blue-900/50 text-blue-200 text-xs rounded-full"
                            >
                              {category}
                            </span>
                          ))}
                          {vendor.service_categories.length > 2 && (
                            <span className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded-full">
                              +{vendor.service_categories.length - 2}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center space-x-1">
                          <Star className="w-4 h-4 text-yellow-400 fill-current" />
                          <span className="text-white font-medium">{vendor.rating.toFixed(1)}</span>
                          <span className="text-gray-400 text-sm">({vendor.review_count})</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleToggleStatus(vendor.id, vendor.is_active)}
                        className="flex items-center space-x-2"
                      >
                        {vendor.is_active ? (
                          <>
                            <ToggleRight className="w-5 h-5 text-green-400" />
                            <span className="text-green-400 text-sm">Active</span>
                          </>
                        ) : (
                          <>
                            <ToggleLeft className="w-5 h-5 text-gray-400" />
                            <span className="text-gray-400 text-sm">Inactive</span>
                          </>
                        )}
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => setSelectedVendor(vendor)}
                          className="p-2 text-blue-400 hover:text-blue-300 hover:bg-gray-700/50 rounded-md"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setEditingVendor(vendor);
                            setShowFormModal(true);
                          }}
                          className="p-2 text-yellow-400 hover:text-yellow-300 hover:bg-gray-700/50 rounded-md"
                          title="Edit Vendor"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteVendor(vendor.id, vendor.name)}
                          className="p-2 text-red-400 hover:text-red-300 hover:bg-gray-700/50 rounded-md"
                          title="Delete Vendor"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vendor Form Modal */}
      {showFormModal && (
        <VendorFormModal
          vendor={editingVendor}
          onClose={() => {
            setShowFormModal(false);
            setEditingVendor(null);
          }}
          onSubmit={editingVendor ? handleUpdateVendor : handleCreateVendor}
        />
      )}

      {/* Vendor Details Modal */}
      {selectedVendor && (
        <div className="fixed inset-0 bg-black/75 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800/95 backdrop-blur-sm rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-semibold text-white">Vendor Details</h3>
              <button
                onClick={() => setSelectedVendor(null)}
                className="p-2 hover:bg-gray-700 rounded-full transition-colors text-gray-400 hover:text-white"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <h4 className="font-medium text-white mb-3">Basic Information</h4>
                  <div className="space-y-2 text-sm">
                    <p><span className="text-gray-400">Name:</span> <span className="text-white">{selectedVendor.name}</span></p>
                    <p><span className="text-gray-400">Contact Person:</span> <span className="text-white">{selectedVendor.contact_person}</span></p>
                    <p><span className="text-gray-400">Email:</span> <span className="text-white">{selectedVendor.email}</span></p>
                    <p><span className="text-gray-400">Phone:</span> <span className="text-white">{selectedVendor.phone}</span></p>
                    <p><span className="text-gray-400">Address:</span> <span className="text-white">{selectedVendor.address}</span></p>
                    {selectedVendor.website && (
                      <p><span className="text-gray-400">Website:</span> 
                        <a href={selectedVendor.website} target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary-600 ml-1">
                          {selectedVendor.website}
                        </a>
                      </p>
                    )}
                  </div>
                </div>

                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <h4 className="font-medium text-white mb-3">Service Categories</h4>
                  <div className="flex flex-wrap gap-2">
                    {selectedVendor.service_categories.map((category, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-blue-900/50 text-blue-200 text-sm rounded-full"
                      >
                        {category}
                      </span>
                    ))}
                  </div>
                </div>

                {selectedVendor.payment_terms && (
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Payment Terms</h4>
                    <p className="text-gray-300 text-sm">{selectedVendor.payment_terms}</p>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                {selectedVendor.photos.length > 0 && (
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Photos</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {selectedVendor.photos.slice(0, 4).map((photo, index) => (
                        <img
                          key={index}
                          src={photo}
                          alt={`${selectedVendor.name} photo ${index + 1}`}
                          className="w-full h-24 object-cover rounded-lg"
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="bg-gray-900/50 p-4 rounded-lg">
                  <h4 className="font-medium text-white mb-3">Rating & Reviews</h4>
                  <div className="flex items-center space-x-2">
                    <Star className="w-5 h-5 text-yellow-400 fill-current" />
                    <span className="text-white font-medium">{selectedVendor.rating.toFixed(1)}</span>
                    <span className="text-gray-400">({selectedVendor.review_count} reviews)</span>
                  </div>
                </div>

                {selectedVendor.contract_details && (
                  <div className="bg-gray-900/50 p-4 rounded-lg">
                    <h4 className="font-medium text-white mb-3">Contract Details</h4>
                    <p className="text-gray-300 text-sm">{selectedVendor.contract_details}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}